import React, { useMemo } from "react";
import { Tooltip } from "primereact/tooltip";
import { ComponentTheme } from "components/fragments/dashboard/components/group-component/GroupComponentConfiguratorNew";
import { Instant, ZonedDateTime, ZoneId } from "@js-joda/core";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { DiffType, DiffValueOptions } from "./ValueChangeIndicatorTypes";
import { formatNumber, getTextColor } from "../charts/utils";
import "./HorizontalBarChart.scss";
import { toInstant } from "utils/joda-time-utils";

interface HorizontalBarChartProps {
  data: DatasetDataInfoResponse;
  diffType: DiffType;
  theme?: ComponentTheme;
  reportMode?: boolean;
  decimalPlaces?: number;
  startOfTheProject?: Instant;
  calculateFromStartOfTheProject?: boolean;
  setCurrentDiffValueOptions: (diffValueOptions: DiffValueOptions) => void;
}

const HorizontalBarChart = (props: HorizontalBarChartProps) => {
  const bars = useMemo(() => {
    const dataset = props.data.data ?? [];
    const grouped = new Map<string, number>();

    const zone = ZoneId.SYSTEM;
    const now = ZonedDateTime.now(zone);
    const firstDayOfCurrentMonth = now
      .withDayOfMonth(1)
      .withHour(0)
      .withMinute(0)
      .withSecond(0)
      .withNano(0);
    const lastFinished = firstDayOfCurrentMonth.minusNanos(1);
    const targetMonth = lastFinished.monthValue();

    let projectStart: ZonedDateTime | null = null;
    if (props.calculateFromStartOfTheProject && props.startOfTheProject) {
      const startInstant = toInstant(props.startOfTheProject);
      projectStart = ZonedDateTime.ofInstant(startInstant, zone);
    }

    const dateRangeStart = dataset[0]?.key;
    const dateRangeEnd = dataset[dataset.length - 1]?.key;

    dataset.forEach(point => {
      const dt = ZonedDateTime.parse(point.key).withZoneSameInstant(zone);
      const year = dt.year();
      const month = dt.monthValue();

      let include = false;

      if (projectStart) {
        const rangeStart = ZonedDateTime.parse(dateRangeStart).withZoneSameInstant(zone);
        const rangeEnd = ZonedDateTime.parse(dateRangeEnd).withZoneSameInstant(zone);

        if (projectStart.isBefore(rangeStart) || projectStart.isAfter(rangeEnd)) {
          include = month <= targetMonth;
        } else {
          include = month >= projectStart.monthValue() && month <= targetMonth;
        }
      } else {
        include = month <= targetMonth;
      }

      if (include) {
        grouped.set(String(year), (grouped.get(String(year)) ?? 0) + point.value);
      }
    });

    const sortedBars = [...grouped.entries()]
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([label, value]) => ({ label, value }));

    if (sortedBars.length >= 2) {
      const first = sortedBars[0];
      const last = sortedBars[sortedBars.length - 1];
      const diffValue =
        props.diffType === DiffType.PERCENTAGE
          ? ((last.value - first.value) / first.value) * 100
          : last.value - first.value;

      props.setCurrentDiffValueOptions({
        value: diffValue,
        currentValueInfo: (
          <div className="flex flex-row gap-2">
            <b>{last.label}:</b>
            <div>
              {formatNumber(last.value, props.decimalPlaces)}
              {props.data.unit ? ` ${props.data.unit}` : ""}
            </div>
          </div>
        ),
        previousValueInfo: (
          <div className="flex flex-row gap-2">
            <b>{first.label}:</b>
            <div>
              {formatNumber(first.value, props.decimalPlaces)}
              {props.data.unit ? ` ${props.data.unit}` : ""}
            </div>
          </div>
        ),
        isHorizontalBarChartDiffLoaded: true,
      });
    } else {
      props.setCurrentDiffValueOptions({
        value: 0,
        currentValueInfo: <>–</>,
        previousValueInfo: <>–</>,
        isHorizontalBarChartDiffLoaded: true,
      });
    }

    return sortedBars;
  }, [
    props.data,
    props.diffType,
    props.decimalPlaces,
    props.data.unit,
    props.setCurrentDiffValueOptions,
  ]);

  const maxValue = Math.max(...bars.map(b => b.value), 0);

  return (
    <div className={`bar-chart-container${props.reportMode ? "-report-mode" : ""}`}>
      {bars.map((bar, i) => {
        const percentage = maxValue > 0 ? (bar.value / maxValue) * 100 : 0;
        const targetId = `bar-fill-${i}`;

        return (
          <div key={bar.label} className="bar-row">
            <Tooltip target={`#${targetId}`} />
            <div className="bar-wrapper">
              <div className="bar-track">
                <div
                  id={targetId}
                  className="bar-fill"
                  style={{ width: `${percentage}%` }}
                  data-pr-tooltip={bar.label}
                  data-pr-position="top"
                >
                  <span className="bar-label-inside">{bar.label}</span>
                </div>
              </div>

              <div className="bar-value" style={{ color: getTextColor(props.theme) }}>
                <div>{formatNumber(bar.value, props.decimalPlaces)}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HorizontalBarChart;
